import { useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { oneOf, searchUpdate } from "@/lib/search";
import { useQuery } from "@/lib/convex";
import type { Id } from "@convex/_generated/dataModel";
import type { PersonActivityStatusValue } from "@convex/schemaEnums";
import {
  LuCircleAlert,
  LuCalendarClock,
  LuCircleCheckBig,
  LuClock3,
  LuChevronDown,
  LuSparkles,
} from "react-icons/lu";
import type { IconType } from "react-icons";
import {
  Badge,
  Box,
  Button,
  Card,
  Collapsible,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { api } from "@convex/_generated/api";
import { MyReviews } from "./ActivityReviews";
import { OverdueTab, overdueFrame } from "../components/OverdueTab";
import { CardGridSkeleton } from "../components/LoadingSkeletons";
import { statusPresentation } from "../components/activityStatus";
import { useEffectivePersonId } from "../impersonation";
import { PageHeader } from "../components/PageHeader";
import { isActivityOverdue } from "@/lib/activityDates";
import { MyRecord } from "./MyRecord";
import { RECORD_ENABLED } from "@/lib/features";
import { tagColor } from "@/lib/tagColor";

type TagSnapshot = {
  tagId: Id<"activityTags">;
  name: string;
  key: string;
  color: string | null;
};

type Assignment = {
  _id: string;
  titleSnapshot: string;
  descriptionSnapshot: string;
  tagSnapshots: TagSnapshot[];
  required: boolean;
  status: PersonActivityStatusValue;
  dueDay: string | null;
  recommendedStartDay: string | null;
  opensOnDay: string | null;
};

/**
 * The page's four columns, in the order somebody works through them: what
 * came back, what is under way, what is out of their hands, what is next.
 * Waiting gathers three states that ask nothing of the person -- submitted
 * work, a prerequisite or opening date, being away -- and each card in it
 * says which. Completed is not a column: it folds away below.
 */
const COLUMNS: Array<{
  key: string;
  statuses: Assignment["status"][];
  title: string;
  description: string;
  icon: IconType;
  tone: string;
}> = [
  {
    key: "needs_revision",
    statuses: ["needs_revision"],
    title: "Needs revision",
    description: "A reviewer asked for changes.",
    icon: LuCircleAlert,
    tone: `${statusPresentation("needs_revision").tone}.solid`,
  },
  {
    key: "in_progress",
    statuses: ["in_progress"],
    title: "In progress",
    description: "Started, not yet submitted.",
    icon: LuClock3,
    tone: `${statusPresentation("in_progress").tone}.solid`,
  },
  {
    key: "waiting",
    statuses: ["awaiting_review", "blocked", "paused"],
    title: "Waiting",
    description: "On someone else, on another activity or a date, or on your return.",
    icon: LuCalendarClock,
    tone: `${statusPresentation("awaiting_review").tone}.solid`,
  },
  {
    key: "upcoming",
    statuses: ["not_started"],
    title: "Upcoming",
    description: "Ready to start.",
    icon: LuSparkles,
    tone: `${statusPresentation("not_started").tone}.solid`,
  },
];

// What a card in the Waiting column is waiting on.
const WAITING_ON: Partial<Record<Assignment["status"], string>> = {
  awaiting_review: "Waiting on someone else",
  blocked: "Waiting on another activity or a date",
  paused: "Paused while you are away",
};

/**
 * Soonest due first -- so what is late leads -- then those with no due date,
 * then by title. They came in the order they were assigned, which is not an
 * order anybody works in.
 */
function byDueDay(left: Assignment, right: Assignment): number {
  if (left.dueDay !== right.dueDay) {
    if (left.dueDay === null) return 1;
    if (right.dueDay === null) return -1;
    return left.dueDay < right.dueDay ? -1 : 1;
  }
  return left.titleSnapshot.localeCompare(right.titleSnapshot);
}

// Days are "YYYY-MM-DD" (convex/days.ts). Parsed at UTC noon so no timezone
// can shunt a deadline onto the day before.
function formatDay(day: string | null) {
  if (day === null) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
  }).format(new Date(`${day}T12:00:00Z`));
}

function TagBadge({ tag }: { tag: TagSnapshot }) {
  return (
    <Badge
      variant="outline"
      style={{ borderColor: tagColor(tag.color), color: tagColor(tag.color) }}
      title={tag.key}
    >
      {tag.name}
    </Badge>
  );
}

/** All of a card's tags, wrapping as they need to. */
function CardTags({ tags }: { tags: TagSnapshot[] }) {
  if (tags.length === 0) {
    return null;
  }
  return (
    <HStack flexWrap="wrap" gap={2}>
      {tags.map((tag) => <TagBadge key={tag.tagId} tag={tag} />)}
    </HStack>
  );
}

/**
 * One activity, as a card that is a link to it.
 *
 * The whole card is the link. It used to carry a "Details" button and an
 * "Open" link to the same page, plus a line of prose explaining what was
 * behind them.
 */
function ActivityCard({
  assignment,
  overdue,
  waitingOn,
}: {
  assignment: Assignment;
  overdue: boolean;
  /** In the Waiting column: on what. */
  waitingOn: string | null;
}) {
  return (
    <Card.Root asChild position="relative" {...overdueFrame(overdue)}>
      <Link to="/activities/$personActivityId" params={{ personActivityId: assignment._id }}>
        {/* The one state not said by the column above. */}
        {overdue ? <OverdueTab /> : null}
        {/* The same room above the title on every card, overdue or not, so
            the tab never pushes one down. Just enough to clear it. */}
        <Card.Body gap={2.5} p={4} pt={5}>
          {/* What it is, beside its title, as a step's is on the activity
              page. */}
          <HStack gap={2} align="baseline" flexWrap="wrap" rowGap={0}>
            <Heading size="md" lineHeight="tight">{assignment.titleSnapshot}</Heading>
            <Text fontSize="sm" color="fg.muted">
              {assignment.required ? "Required" : "Encouraged"}
            </Text>
          </HStack>

          {waitingOn === null ? null : (
            <Text fontSize="sm" color="fg.muted">{waitingOn}</Text>
          )}

          <CardTags tags={assignment.tagSnapshots} />

          {/* One line, not three bordered boxes. Dates are what people came
              to read, so they are the last thing on the card and read left to
              right in the order they happen. */}
          <HStack flexWrap="wrap" gap={1.5} fontSize="xs" color="fg.muted" mt="auto" pt={1}>
            {assignment.recommendedStartDay !== null ? (
              <Text>Start {formatDay(assignment.recommendedStartDay)}</Text>
            ) : null}
            {assignment.recommendedStartDay !== null && assignment.dueDay !== null ? (
              <Text aria-hidden>·</Text>
            ) : null}
            {assignment.dueDay !== null ? (
              <Text color={overdue ? "red.600" : undefined} fontWeight={overdue ? "semibold" : undefined}>
                Due {formatDay(assignment.dueDay)}
              </Text>
            ) : null}
            {assignment.opensOnDay !== null ? (
              <>
                <Text aria-hidden>·</Text>
                <Text>Opens {formatDay(assignment.opensOnDay)}</Text>
              </>
            ) : null}
          </HStack>
        </Card.Body>
      </Link>
    </Card.Root>
  );
}

/** What is finished, folded away below the columns until asked for. */
function CompletedSection({ items }: { items: Assignment[] }) {
  if (items.length === 0) {
    return null;
  }
  const look = statusPresentation("completed");
  return (
    <Collapsible.Root as="section">
      <Collapsible.Trigger asChild>
        <Button variant="plain" px={0} gap={2}>
          <HStack gap={2} fontSize="sm" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color={`${look.tone}.solid`}>
            <Icon as={LuCircleCheckBig} boxSize={4} />
            Completed
            <Text as="span" color="fg.muted">· {items.length}</Text>
          </HStack>
          <Collapsible.Indicator color="fg.muted" transition="transform 0.15s" _open={{ transform: "rotate(180deg)" }}>
            <LuChevronDown />
          </Collapsible.Indicator>
        </Button>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <Text mb={3} fontSize="sm" color="fg.muted">All required steps are done. Nothing more is needed from you.</Text>
        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap={4}>
          {items.map((assignment) => (
            <ActivityCard key={assignment._id} assignment={assignment} overdue={false} waitingOn={null} />
          ))}
        </SimpleGrid>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}

const ACTIVITIES_TABS = ["activities", "reviews", "record"] as const;

/** "1 activity", "3 activities". */
function activities(count: number): string {
  return `${count} ${count === 1 ? "activity" : "activities"}`;
}

export function validateActivitiesSearch(search: Record<string, unknown>): { tab?: (typeof ACTIVITIES_TABS)[number] } {
  const tab = oneOf(search.tab, ACTIVITIES_TABS);
  // A link to the record while it is switched off opens the activities.
  return { tab: tab === "record" && !RECORD_ENABLED ? undefined : tab };
}

export function ActivitiesPage() {
  const effectivePersonId = useEffectivePersonId();
  const assignments = useQuery(api.personActivities.listForViewer, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  }) as Assignment[] | undefined;
  const summary = useQuery(api.personActivities.viewerSummary, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });

  // Shared with the dashboard, which asks the same question. See
  // src/lib/activityDates.ts for why this cannot live in the query.
  const isOverdue = (assignment: Assignment) => isActivityOverdue(assignment);

  // Computed here rather than in the query: overdue is a comparison with
  // today, and a Convex query is cached until its data changes -- so a count
  // computed server-side would go stale at midnight and stay stale until
  // something unrelated wrote to the table.
  const overdueCount = useMemo(
    () => (assignments ?? []).filter(isOverdue).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assignments],
  );

  // Which half to open on. Cached by the query layer, so asking here as well
  // as inside the record half costs nothing.
  const records = useQuery(api.participations.listForPerson, RECORD_ENABLED
    ? { personId: null, effectivePersonId: effectivePersonId as Id<"persons"> | null }
    : "skip");

  // Decided ONCE, when both queries have first answered, and never revisited:
  // somebody with no activities should not have to find the tab that concerns
  // them, and equally a tab that moved under them as a query settled would be
  // worse than one that opened on the wrong half.
  // The steps on other people's activities that wait on the viewer, and
  // whether they could ever have any: the tab is there for a supervisor or
  // co-supervisor, and for anybody something is waiting on, and for nobody
  // else -- a tab that is always empty is a tab to wonder about.
  const pending = useQuery(api.reviews.listPendingForViewer, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });
  const supervision = useQuery(api.reviews.viewerSupervisesAnyone, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });
  const coSupervision = useQuery(api.coSupervision.viewerCoSupervisesAnyone, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });
  const reviewCount = pending?.length ?? 0;
  const reviews = reviewCount > 0 || Boolean(supervision?.supervises) || Boolean(coSupervision?.coSupervises);

  // A group head with nothing of their own opens on what waits on them, and
  // somebody with no activities but a record opens on the record.
  const [autoHalf, setAutoHalf] = useState<string | null>(null);
  if (autoHalf === null && assignments !== undefined && pending !== undefined
    && (records !== undefined || !RECORD_ENABLED)) {
    setAutoHalf(
      assignments.length === 0 && pending.length > 0
        ? "reviews"
        : assignments.length === 0 && (records?.length ?? 0) > 0
          ? "record"
          : "activities",
    );
  }
  // A tab somebody chose is in the address (?tab=record) and wins over the
  // one picked for them.
  const chosenHalf = useSearch({ from: "/_authed/activities" }).tab;
  const half = chosenHalf ?? autoHalf;
  const navigateHere = useNavigate({ from: "/activities" });
  const setHalf = (next: string) =>
    void navigateHere({ search: { tab: oneOf(next, ACTIVITIES_TABS) }, ...searchUpdate });

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="Events &amp; Activities"
        description={RECORD_ENABLED
          ? "Your activities and reviews, and the events and courses you have taken part in."
          : "Your activities, and the steps on others' that wait on you."}
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "Events & Activities" }]}
        // Each count says what it counts, now that the page holds reviews as
        // well as activities, and a zero is left out rather than shown.
        actions={
          summary
            ? (
              <HStack flexWrap="wrap" gap={3}>
                {summary.completed > 0 ? (
                  <Badge variant="subtle" colorPalette="gray">{activities(summary.completed)} completed</Badge>
                ) : null}
                {summary.awaitingReview > 0 ? (
                  <Badge variant="subtle" colorPalette="gray">{activities(summary.awaitingReview)} waiting on someone else</Badge>
                ) : null}
                {overdueCount > 0 ? (
                  <Badge variant="subtle" colorPalette="orange">{activities(overdueCount)} overdue</Badge>
                ) : null}
                {reviewCount > 0 ? (
                  <Badge variant="subtle" colorPalette="blue">
                    {reviewCount} {reviewCount === 1 ? "review" : "reviews"} waiting on you
                  </Badge>
                ) : null}
              </HStack>
            )
            : null
        }
      />

      {/*
        Two halves of equal billing, not one with the other bolted underneath.
        For most of the institute the record is the ONLY half with anything in
        it -- no activities at all, and a list of workshops, conferences and
        seminars -- and stacking would leave them scrolling past an empty
        activity list to reach the part that concerns them (model §17).

        Tabs rather than columns because either may legitimately be empty, and
        an empty column beside a full one reads as something failing to load.
      */}
      <Tabs.Root
        value={half ?? "activities"}
        onValueChange={(e) => setHalf(e.value)}
        px={{ base: 6, sm: 8, lg: 10 }}
        pt={{ base: 6, lg: 8 }}
      >
        <Tabs.List>
          <Tabs.Trigger value="activities">My activities</Tabs.Trigger>
          {reviews || half === "reviews" ? (
            <Tabs.Trigger value="reviews">
              My reviews
              {reviewCount > 0 ? <Badge colorPalette="blue" variant="solid">{reviewCount}</Badge> : null}
            </Tabs.Trigger>
          ) : null}
          {RECORD_ENABLED ? <Tabs.Trigger value="record">My record</Tabs.Trigger> : null}
        </Tabs.List>

        <Tabs.Content value="reviews" px={0} pt={6}>
          <MyReviews />
        </Tabs.Content>

        {RECORD_ENABLED ? (
          <Tabs.Content value="record" px={0} pt={6}>
            <MyRecord effectivePersonId={effectivePersonId as Id<"persons"> | null} />
          </Tabs.Content>
        ) : null}

        <Tabs.Content value="activities" px={0} pt={6}>
      <Stack gap={8} pb={{ base: 6, lg: 8 }}>
        {assignments === undefined ? (
          <CardGridSkeleton label="Loading activities" cards={8} columns={{ base: 1, md: 2, xl: 4 }} />
        ) : (
          <>
            {/* Four columns side by side on wide screens, two on medium
                ones, one on a phone. A column with nothing in it still shows,
                so the four keep their places. */}
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap={6} alignItems="start">
              {COLUMNS.map((column) => {
                const items = (assignments ?? [])
                  .filter((assignment) => column.statuses.includes(assignment.status))
                  .sort(byDueDay);
                return (
                  <Stack as="section" key={column.key} gap={3}>
                    {/* The count with the heading it counts. */}
                    <Box>
                      <HStack gap={2} fontSize="sm" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color={column.tone}>
                        <Icon as={column.icon} boxSize={4} />
                        {column.title}
                        <Text as="span" color="fg.muted">· {items.length}</Text>
                      </HStack>
                      <Text mt={1} fontSize="sm" color="fg.muted">{column.description}</Text>
                    </Box>
                    {items.length === 0 ? (
                      <Text fontSize="sm" color="fg.subtle">Nothing here.</Text>
                    ) : items.map((assignment) => (
                      <ActivityCard
                        key={assignment._id}
                        assignment={assignment}
                        overdue={isOverdue(assignment)}
                        waitingOn={column.key === "waiting" ? WAITING_ON[assignment.status] ?? null : null}
                      />
                    ))}
                  </Stack>
                );
              })}
            </SimpleGrid>

            <CompletedSection items={(assignments ?? []).filter((assignment) => assignment.status === "completed").sort(byDueDay)} />
          </>
        )}
      </Stack>
        </Tabs.Content>
      </Tabs.Root>
    </Box>
  );
}
